import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Plus,
  Settings,
  Layers,
  Package,
  Eye,
  EyeOff,
  Edit,
  Trash2,
  Copy,
  GripVertical,
  ChevronRight,
  ChevronDown,
  Save,
  X,
  AlertCircle,
  Check,
  Zap,
  Palette,
  ShoppingBag,
  List,
  Tag,
  FileText,
  AlertTriangle,
  Loader2
} from 'lucide-react';
import { Header } from '../../components/Header';
import QuickBundlesManager from '../../components/admin/QuickBundlesManager';
import ConditionalSkuRulesEditor from '../../components/admin/ConditionalSkuRulesEditor';
import ProductFamilyTabs, { getProductFamilyMeta } from '../../components/ProductFamilyTabs';
import DuplicateToFamilyDialog from '../../components/DuplicateToFamilyDialog';
import FamilyScopePicker, { CrossFamilyWarning } from '../../components/FamilyScopePicker';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
} from '../../components/ui/alert-dialog';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

import { API_BASE } from '../../lib/apiBase';
const API_URL = API_BASE;
const getToken = () => localStorage.getItem('token');

// Mode icons and colors
const MODE_CONFIG = {
  quick: { icon: Zap, color: 'bg-amber-100 text-amber-700', label: 'Quick Order' },
  visual: { icon: Palette, color: 'bg-indigo-100 text-indigo-700', label: 'Visual Builder' },
  expert: { icon: Settings, color: 'bg-slate-100 text-slate-700', label: 'Expert Mode' }
};

// UI Type options
const UI_TYPES = [
  { value: 'cards', label: 'Cards' },
  { value: 'dropdown', label: 'Dropdown' },
  { value: 'toggles', label: 'Toggles' },
  { value: 'multi-select', label: 'Multi-Select' }
];

// Attribute Selector Editor for an Option
function AttributeSelectorEditor({ selectors = [], attributeLists, onChange }) {
  const addSelector = () => {
    const newSelector = {
      field_id: `attr_${Date.now()}`,
      label: '',
      source_list_id: '',
      selection_mode: 'single',
      required: false,
      max_selections: null,
      help_text: '',
      placeholder: ''
    };
    onChange([...selectors, newSelector]);
  };

  const updateSelector = (index, updates) => {
    const newSelectors = [...selectors];
    newSelectors[index] = { ...newSelectors[index], ...updates };
    onChange(newSelectors);
  };

  const removeSelector = (index) => {
    onChange(selectors.filter((_, i) => i !== index));
  };

  return (
    <div className="space-y-3">
      {selectors.length === 0 ? (
        <p className="text-sm text-slate-500 italic">No attribute selectors configured</p>
      ) : (
        selectors.map((selector, index) => (
          <div key={selector.field_id || index} className="p-3 bg-slate-50 rounded-lg border border-slate-200 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700 flex items-center gap-2">
                <Tag className="w-4 h-4 text-indigo-500" />
                Attribute Selector #{index + 1}
              </span>
              <button
                type="button"
                onClick={() => removeSelector(index)}
                className="p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Field ID */}
              <div>
                <label className="block text-xs text-slate-500 mb-1">Field ID *</label>
                <input
                  type="text"
                  value={selector.field_id || ''}
                  onChange={(e) => updateSelector(index, { field_id: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_') })}
                  placeholder="e.g., pantone_color"
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              
              {/* Label */}
              <div>
                <label className="block text-xs text-slate-500 mb-1">Label *</label>
                <input
                  type="text"
                  value={selector.label || ''}
                  onChange={(e) => updateSelector(index, { label: e.target.value })}
                  placeholder="e.g., Pantone Color"
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              
              {/* Source List */}
              <div>
                <label className="block text-xs text-slate-500 mb-1">Source List *</label>
                <select
                  value={selector.source_list_id || ''}
                  onChange={(e) => updateSelector(index, { source_list_id: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500 bg-white"
                >
                  <option value="">Select a list...</option>
                  {attributeLists.map(list => (
                    <option key={list.slug} value={list.slug}>
                      {list.name} ({list.item_count || 0} items)
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Selection Mode */}
              <div>
                <label className="block text-xs text-slate-500 mb-1">Selection Mode</label>
                <select
                  value={selector.selection_mode || 'single'}
                  onChange={(e) => updateSelector(index, { selection_mode: e.target.value })}
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500 bg-white"
                >
                  <option value="single">Single Select</option>
                  <option value="multi">Multi Select</option>
                </select>
              </div>
            </div>
            
            <div className="grid grid-cols-3 gap-3">
              {/* Required Toggle */}
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={selector.required || false}
                  onChange={(e) => updateSelector(index, { required: e.target.checked })}
                  className="rounded border-slate-300 text-indigo-600"
                />
                <span className="text-sm text-slate-700">Required</span>
              </label>
              
              {/* Max Selections (for multi) */}
              {selector.selection_mode === 'multi' && (
                <div>
                  <label className="block text-xs text-slate-500 mb-1">Max Selections</label>
                  <input
                    type="number"
                    min="1"
                    value={selector.max_selections || ''}
                    onChange={(e) => updateSelector(index, { max_selections: e.target.value ? parseInt(e.target.value) : null })}
                    placeholder="No limit"
                    className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500"
                  />
                </div>
              )}
            </div>
            
            {/* Help Text & Placeholder */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-xs text-slate-500 mb-1">Placeholder</label>
                <input
                  type="text"
                  value={selector.placeholder || ''}
                  onChange={(e) => updateSelector(index, { placeholder: e.target.value })}
                  placeholder="Select..."
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500"
                />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1">Help Text</label>
                <input
                  type="text"
                  value={selector.help_text || ''}
                  onChange={(e) => updateSelector(index, { help_text: e.target.value })}
                  placeholder="Optional guidance for users"
                  className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:ring-1 focus:ring-indigo-500"
                />
              </div>
            </div>
          </div>
        ))
      )}
      
      <button
        type="button"
        onClick={addSelector}
        className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
      >
        <Plus className="w-4 h-4" />
        Add Attribute Selector
      </button>
    </div>
  );
}

// Step Editor Modal
function StepEditorModal({ step, assets, rateCardItems, attributeLists, otherSteps = [], productFamily = 'vinyl', onSave, onClose }) {
  const [formData, setFormData] = useState({
    step_id: step?.step_id || '',
    label: step?.label || '',
    description: step?.description || '',
    asset_category: step?.category_id || step?.asset_category || '',
    ui_type: step?.ui_type || 'cards',
    selection_mode: step?.selection_mode || 'single',
    is_required: step?.is_required ?? true,
    options: step?.options || [],
    // Expert Setup metadata
    expert_group: step?.expert_group || '',
    expert_group_label: step?.expert_group_label || '',
    expert_display_order: step?.expert_display_order ?? null
  });
  
  // Track which option is expanded for attribute selector editing
  const [expandedOptionId, setExpandedOptionId] = useState(null);

  // Get unique categories from assets (using category_id)
  // Filter out any undefined/null/empty string values
  const categories = [...new Set(
    assets
      .map(a => a.category_id)
      .filter(cat => cat && typeof cat === 'string' && cat.trim() !== '')
  )];
  
  // Get options for selected category
  const categoryAssets = assets.filter(a => a.category_id === formData.asset_category);

  const handleCategoryChange = (category) => {
    const categoryOptions = assets.filter(a => a.category_id === category);
    setFormData(prev => ({
      ...prev,
      asset_category: category,
      step_id: prev.step_id || (category ? category.replace(/_/g, '_') : ''),
      options: categoryOptions.map((asset, idx) => ({
        option_id: asset.option_id,
        is_default: idx === 0,
        is_included_in_base: false,
        pricing_mapping: null,
        additional_fields: [] // Initialize with empty attribute selectors
      }))
    }));
  };

  const toggleOption = (optionId) => {
    setFormData(prev => {
      const exists = prev.options.find(o => o.option_id === optionId);
      if (exists) {
        return { ...prev, options: prev.options.filter(o => o.option_id !== optionId) };
      } else {
        return { ...prev, options: [...prev.options, { option_id: optionId, is_default: false, pricing_mapping: null, additional_fields: [] }] };
      }
    });
  };

  const setDefaultOption = (optionId) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map(o => ({
        ...o,
        is_default: o.option_id === optionId
      }))
    }));
  };

  // Toggle mandatory flag for an option (mandatory options are always selected and cannot be deselected)
  const toggleMandatory = (optionId) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map(o => 
        o.option_id === optionId 
          ? { ...o, is_mandatory: !o.is_mandatory }
          : o
      )
    }));
  };

  const updateOptionPricing = (optionId, pricingData) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map(o => 
        o.option_id === optionId 
          ? { ...o, pricing_mapping: pricingData, is_included_in_base: pricingData?.type === 'included' }
          : o
      )
    }));
  };

  // Update attribute selectors for an option (stored in additional_fields for backward compatibility)
  const updateOptionAttributeSelectors = (optionId, selectors) => {
    setFormData(prev => ({
      ...prev,
      options: prev.options.map(o => 
        o.option_id === optionId 
          ? { ...o, additional_fields: selectors }
          : o
      )
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.step_id || !formData.label || !formData.asset_category) {
      alert('Please fill in all required fields');
      return;
    }
    // Send both category_id and asset_category for compatibility
    // Include expert metadata fields
    onSave({
      ...formData,
      category_id: formData.asset_category,
      expert_group: formData.expert_group || null,
      expert_group_label: formData.expert_group_label || null,
      expert_display_order: formData.expert_display_order !== '' && formData.expert_display_order !== null 
        ? parseInt(formData.expert_display_order) 
        : null
    });
  };

  // Get the asset label for an option
  const getAssetLabel = (optionId) => {
    const asset = categoryAssets.find(a => a.option_id === optionId);
    return asset?.label || optionId;
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[60] p-4">
      <div className="bg-card rounded-xl shadow-premium-modal w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-border">
          <h3 className="text-lg font-semibold">{step ? 'Edit Step' : 'Add Step'}</h3>
          <button onClick={onClose} className="p-1 hover:bg-secondary rounded">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Step ID *</label>
              <input
                type="text"
                value={formData.step_id}
                onChange={(e) => setFormData(prev => ({ ...prev, step_id: e.target.value }))}
                placeholder="e.g., record_color"
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Label *</label>
              <input
                type="text"
                value={formData.label}
                onChange={(e) => setFormData(prev => ({ ...prev, label: e.target.value }))}
                placeholder="e.g., Vinyl Color"
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <input
              type="text"
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Brief description shown to customers"
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
            />
          </div>

          {/* Asset Category */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Asset Category *</label>
            <select
              value={formData.asset_category}
              onChange={(e) => handleCategoryChange(e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
              required
            >
              <option value="">Select a category...</option>
              {categories.map(cat => {
                const displayLabel = cat && typeof cat === 'string' 
                  ? cat.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
                  : String(cat || '');
                return (
                  <option key={cat} value={cat}>{displayLabel}</option>
                );
              })}
            </select>
            <p className="text-xs text-gray-500 mt-1">Links to Visual Builder Assets</p>
          </div>

          {/* UI Configuration */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">UI Type</label>
              <select
                value={formData.ui_type}
                onChange={(e) => setFormData(prev => ({ ...prev, ui_type: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
              >
                {UI_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Selection</label>
              <select
                value={formData.selection_mode}
                onChange={(e) => setFormData(prev => ({ ...prev, selection_mode: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
              >
                <option value="single">Single Select</option>
                <option value="multi">Multi Select</option>
              </select>
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_required}
                  onChange={(e) => setFormData(prev => ({ ...prev, is_required: e.target.checked }))}
                  className="rounded border-border"
                />
                <span className="text-sm text-gray-700">Required</span>
              </label>
            </div>
          </div>

          {/* Expert Setup Metadata */}
          <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
            <h4 className="text-sm font-medium text-slate-700 mb-3 flex items-center gap-2">
              <Settings className="w-4 h-4" />
              Expert Setup Configuration
            </h4>
            <p className="text-xs text-slate-500 mb-4">
              Configure how this step appears in Expert Setup mode. Steps with the same group are displayed together.
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Group ID</label>
                <input
                  type="text"
                  value={formData.expert_group}
                  onChange={(e) => setFormData(prev => ({ ...prev, expert_group: e.target.value }))}
                  placeholder="e.g., vinyl_specs"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
                />
                <p className="text-xs text-slate-400 mt-1">Steps with same ID are grouped</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Group Label</label>
                <input
                  type="text"
                  value={formData.expert_group_label}
                  onChange={(e) => setFormData(prev => ({ ...prev, expert_group_label: e.target.value }))}
                  placeholder="e.g., Vinyl Specifications"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
                />
                <p className="text-xs text-slate-400 mt-1">Display name for the group</p>
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">Display Order</label>
                <input
                  type="number"
                  min="0"
                  value={formData.expert_display_order ?? ''}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    expert_display_order: e.target.value === '' ? null : parseInt(e.target.value)
                  }))}
                  placeholder="Auto"
                  className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
                />
                <p className="text-xs text-slate-400 mt-1">Order within group (0 = first)</p>
              </div>
            </div>
          </div>

          {/* Options Selection with Pricing Mapping */}
          {formData.asset_category && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Options, Pricing & Attributes ({formData.options.length} selected)
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Map each option to a Sales SKU for pricing. Click "Configure Attributes" to add metadata fields (Pantone colors, etc.) that don't affect pricing.
              </p>
              <div className="border border-border rounded-lg divide-y divide-border">
                {categoryAssets.map(asset => {
                  const optionConfig = formData.options.find(o => o.option_id === asset.option_id);
                  const isSelected = !!optionConfig;
                  const isDefault = optionConfig?.is_default;
                  const isMandatory = optionConfig?.is_mandatory;
                  const pricing = optionConfig?.pricing_mapping;
                  const hasAttributes = optionConfig?.additional_fields?.length > 0;
                  const isExpanded = expandedOptionId === asset.option_id;
                  
                  return (
                    <div
                      key={asset.option_id}
                      className={`p-3 ${isSelected ? 'bg-indigo-50/50' : 'hover:bg-secondary'}`}
                    >
                      {/* Option header row */}
                      <div className="flex items-center justify-between mb-2">
                        <label className="flex items-center gap-3 flex-1 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleOption(asset.option_id)}
                            className="rounded border-border"
                          />
                          <span className="font-medium">{asset.label}</span>
                          <span className="text-xs text-gray-500">({asset.option_id})</span>
                          {isMandatory && (
                            <span className="text-xs px-1.5 py-0.5 bg-red-100 text-red-700 rounded font-medium">
                              Mandatory
                            </span>
                          )}
                          {hasAttributes && (
                            <span className="text-xs px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded">
                              {optionConfig.additional_fields.length} attr
                            </span>
                          )}
                          {(optionConfig?.pricing_mapping?.sku_rules?.length > 0) && (
                            <span
                              className="text-xs px-1.5 py-0.5 bg-indigo-100 text-indigo-700 rounded"
                              title="This option resolves to a different Sales SKU based on earlier selections"
                              data-testid={`conditional-sku-badge-${asset.option_id}`}
                            >
                              {optionConfig.pricing_mapping.sku_rules.length} conditional SKU
                              {optionConfig.pricing_mapping.sku_rules.length === 1 ? '' : 's'}
                            </span>
                          )}
                        </label>
                        <div className="flex items-center gap-2">
                          {isSelected && (
                            <>
                              <button
                                type="button"
                                onClick={() => setExpandedOptionId(isExpanded ? null : asset.option_id)}
                                className="text-xs px-2 py-1 rounded bg-purple-50 text-purple-600 hover:bg-purple-100 flex items-center gap-1"
                                data-testid={`config-attributes-${asset.option_id}`}
                              >
                                <Tag className="w-3 h-3" />
                                {isExpanded ? 'Hide Attributes' : 'Configure Attributes'}
                              </button>
                              <button
                                type="button"
                                onClick={() => toggleMandatory(asset.option_id)}
                                className={`text-xs px-2 py-1 rounded ${optionConfig?.is_mandatory ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                title="Mandatory options are always selected and cannot be deselected by customers"
                                data-testid={`mandatory-${asset.option_id}`}
                              >
                                {optionConfig?.is_mandatory ? 'Mandatory' : 'Set Mandatory'}
                              </button>
                              <button
                                type="button"
                                onClick={() => setDefaultOption(asset.option_id)}
                                className={`text-xs px-2 py-1 rounded ${isDefault ? 'bg-indigo-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                              >
                                {isDefault ? 'Default' : 'Set Default'}
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      
                      {/* Pricing mapping row (only shown if selected) */}
                      {isSelected && (
                        <div className="ml-7 grid grid-cols-3 gap-3">
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Sales SKU</label>
                            <select
                              value={pricing?.sales_sku || ''}
                              onChange={(e) => updateOptionPricing(asset.option_id, {
                                ...pricing,
                                sales_sku: e.target.value || null,
                                type: pricing?.type || 'addon',
                                quantity_multiplier: pricing?.quantity_multiplier || 1
                              })}
                              className="w-full px-2 py-1.5 text-sm border border-border rounded focus:ring-1 focus:ring-accent/20 focus:border-accent"
                            >
                              <option value="">No SKU (included)</option>
                              {rateCardItems.map(item => (
                                <option key={item.sales_sku} value={item.sales_sku}>
                                  {item.sales_sku} - {item.display_name}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Pricing Type</label>
                            <select
                              value={pricing?.type || 'addon'}
                              onChange={(e) => updateOptionPricing(asset.option_id, {
                                ...pricing,
                                type: e.target.value,
                                quantity_multiplier: pricing?.quantity_multiplier || 1
                              })}
                              className="w-full px-2 py-1.5 text-sm border border-border rounded focus:ring-1 focus:ring-accent/20 focus:border-accent"
                            >
                              <option value="base">Base Product</option>
                              <option value="addon">Add-on</option>
                              <option value="one_time_amortized">One-time (Amortized)</option>
                              <option value="included">Included (no extra cost)</option>
                            </select>
                          </div>
                          <div>
                            <label className="block text-xs text-gray-500 mb-1">Qty Multiplier</label>
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={pricing?.quantity_multiplier || 1}
                              onChange={(e) => updateOptionPricing(asset.option_id, {
                                ...pricing,
                                quantity_multiplier: parseFloat(e.target.value) || 1
                              })}
                              className="w-full px-2 py-1.5 text-sm border border-border rounded focus:ring-1 focus:ring-accent/20 focus:border-accent"
                            />
                          </div>
                          {/* Fixed amount input — only relevant for
                              one_time_amortized. Left blank falls back
                              to the SKU's tier price at qty=1. Occupies
                              the full grid row so the amber helper text
                              has room to breathe. */}
                          {pricing?.type === 'one_time_amortized' && (
                            <div className="col-span-3 flex items-end gap-3 pt-1">
                              <div className="flex-1">
                                <label className="block text-xs text-gray-500 mb-1">
                                  Fixed amount (EUR){' '}
                                  <span className="text-gray-400 font-normal">
                                    — leave blank to use SKU tier @ qty=1
                                  </span>
                                </label>
                                <input
                                  type="number"
                                  min="0"
                                  step="0.01"
                                  placeholder="e.g. 66.44 → distributed over qty"
                                  value={pricing?.fixed_amount ?? ''}
                                  onChange={(e) => updateOptionPricing(asset.option_id, {
                                    ...pricing,
                                    fixed_amount: e.target.value === ''
                                      ? null
                                      : parseFloat(e.target.value),
                                  })}
                                  className="w-full px-2 py-1.5 text-sm border border-amber-300 rounded focus:ring-1 focus:ring-amber-200 bg-amber-50/40"
                                  data-testid={`pricing-fixed-amount-${asset.option_id}`}
                                />
                              </div>
                              <div className="text-[11px] text-amber-700 max-w-xs pb-1">
                                Total is billed as a flat lump-sum,
                                distributed across the selected quantity.
                              </div>
                            </div>
                          )}
                          {/* Conditional SKU rules — swaps the Sales
                              SKU based on earlier flow selections /
                              attributes. Only shown when a default
                              SKU is set (rules without a fallback
                              would be ambiguous). */}
                          {pricing?.sales_sku && (
                            <div className="col-span-3">
                              <ConditionalSkuRulesEditor
                                rules={pricing?.sku_rules || []}
                                onChange={(nextRules) => updateOptionPricing(asset.option_id, {
                                  ...pricing,
                                  sku_rules: nextRules,
                                })}
                                otherSteps={otherSteps}
                                productFamily={productFamily}
                                rateCardItems={rateCardItems}
                              />
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Attribute Selectors Section (expanded) */}
                      {isSelected && isExpanded && (
                        <div className="ml-7 mt-4 p-3 bg-purple-50/50 rounded-lg border border-purple-200">
                          <div className="flex items-center gap-2 mb-3">
                            <List className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-medium text-purple-800">Attribute Selectors</span>
                          </div>
                          <p className="text-xs text-purple-600 mb-3">
                            Add fields that collect additional information when this option is selected. These are metadata fields that don't affect pricing (e.g., Pantone color selection, custom text).
                          </p>
                          <AttributeSelectorEditor
                            selectors={optionConfig?.additional_fields || []}
                            attributeLists={attributeLists}
                            onChange={(newSelectors) => updateOptionAttributeSelectors(asset.option_id, newSelectors)}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Pricing mapping legend */}
              <div className="mt-2 p-3 bg-blue-50 rounded-lg">
                <p className="text-xs text-blue-700 font-medium mb-1">Pricing Type Guide:</p>
                <ul className="text-xs text-blue-600 space-y-0.5">
                  <li>• <strong>Base Product</strong>: The main product SKU - determines the base price (e.g., 12" LP vinyl)</li>
                  <li>• <strong>Add-on</strong>: Extra cost added to base (e.g., 4-Colour Label Print)</li>
                  <li>• <strong>One-time (Amortized)</strong>: Fixed lump-sum distributed across the quantity (e.g. freight, glass mastering, tooling)</li>
                  <li>• <strong>Included</strong>: No additional charge (e.g., Unprinted Label)</li>
                </ul>
              </div>
            </div>
          )}
        </form>

        <div className="flex justify-end gap-3 p-4 border-t border-border bg-secondary/30">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-secondary rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Save className="w-4 h-4" />
            Save Step
          </button>
        </div>
      </div>
    </div>
  );
}

// Flow Editor Panel
function FlowEditor({ flow, assets, rateCards: initialRateCards, attributeLists, activeFamily = 'vinyl', onSave, onClose }) {
  const [formData, setFormData] = useState({
    name: flow?.name || '',
    mode: flow?.mode || 'visual',
    // Phase 2: when creating a NEW flow, default to the active tab's family
    // so admin doesn't accidentally save a Blu-ray flow as Vinyl.
    product_family: flow?.product_family || activeFamily || 'vinyl',
    description: flow?.description || '',
    default_rate_card_id: flow?.default_rate_card_id || '',
    steps: flow?.steps || [],
    is_active: flow?.is_active ?? true,
    audience: flow?.audience || 'all'
  });
  const [editingStep, setEditingStep] = useState(null);
  const [showStepModal, setShowStepModal] = useState(false);
  const [rateCardItems, setRateCardItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  // Phase 2 (soft scoping): independent family scope for the Default Rate
  // Card picker. Default = 'current' (= the flow's product_family) but
  // admin can override to 'all' or any specific family. Cross-family
  // selection is allowed; only a warning chip is shown.
  const [rateCards, setRateCards] = useState(initialRateCards || []);
  // When EDITING an existing flow we widen the initial scope to 'all' so a
  // pre-existing cross-family rate-card selection remains visible. When
  // CREATING a new flow we keep the default 'current'.
  const [rateCardScope, setRateCardScope] = useState(flow?.id ? 'all' : 'current');

  useEffect(() => {
    let cancelled = false;
    const fetchRateCardsForScope = async () => {
      try {
        const resolved =
          rateCardScope === 'current' ? (formData.product_family || 'vinyl') : rateCardScope;
        const res = await fetch(
          `${API_URL}/api/sales/rate-cards?product_family=${encodeURIComponent(resolved)}`,
          { headers: { 'Authorization': `Bearer ${getToken()}` } }
        );
        if (!cancelled && res.ok) {
          const data = await res.json();
          setRateCards(data || []);
          // Only auto-clear when the currently-selected rate card disappears
          // from the list AND the scope is 'current'. When admin manually
          // widened the scope, never auto-clear — they may have intentionally
          // picked a cross-family rate card.
          if (rateCardScope === 'current' && formData.default_rate_card_id) {
            const stillValid = (data || []).some(rc => rc.id === formData.default_rate_card_id);
            if (!stillValid) {
              setFormData(prev => ({ ...prev, default_rate_card_id: '' }));
            }
          }
        }
      } catch (err) {
        console.error('Failed to refetch rate cards for scope:', err);
      }
    };
    fetchRateCardsForScope();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.product_family, rateCardScope]);

  // Stash the currently-selected rate card object so we can render its
  // family + status badges and detect cross-family selection.
  const selectedRateCard = rateCards.find(rc => rc.id === formData.default_rate_card_id);
  const isCrossFamily =
    selectedRateCard &&
    (selectedRateCard.product_family || 'vinyl') !== (formData.product_family || 'vinyl');

  // Fetch rate card items when rate card changes
  useEffect(() => {
    const fetchRateCardItems = async () => {
      if (!formData.default_rate_card_id) {
        setRateCardItems([]);
        return;
      }
      
      setLoadingItems(true);
      try {
        const res = await fetch(
          `${API_URL}/api/sales/rate-cards/${formData.default_rate_card_id}/items?limit=500`,
          { headers: { 'Authorization': `Bearer ${getToken()}` } }
        );
        if (res.ok) {
          const items = await res.json();
          setRateCardItems(items);
        }
      } catch (err) {
        console.error('Failed to fetch rate card items:', err);
      } finally {
        setLoadingItems(false);
      }
    };
    
    fetchRateCardItems();
  }, [formData.default_rate_card_id]);

  const handleAddStep = () => {
    setEditingStep(null);
    setShowStepModal(true);
  };

  const handleEditStep = (step, index) => {
    setEditingStep({ ...step, _index: index });
    setShowStepModal(true);
  };

  const handleSaveStep = (stepData) => {
    setFormData(prev => {
      const newSteps = [...prev.steps];
      if (editingStep?._index !== undefined) {
        newSteps[editingStep._index] = { ...stepData, display_order: editingStep._index + 1 };
      } else {
        newSteps.push({ ...stepData, display_order: newSteps.length + 1 });
      }
      return { ...prev, steps: newSteps };
    });
    setShowStepModal(false);
    setEditingStep(null);
  };

  const handleDeleteStep = (index) => {
    if (!window.confirm('Delete this step?')) return;
    setFormData(prev => ({
      ...prev,
      steps: prev.steps.filter((_, i) => i !== index)
    }));
  };

  const moveStep = (index, direction) => {
    setFormData(prev => {
      const newSteps = [...prev.steps];
      const newIndex = index + direction;
      if (newIndex < 0 || newIndex >= newSteps.length) return prev;
      [newSteps[index], newSteps[newIndex]] = [newSteps[newIndex], newSteps[index]];
      return { ...prev, steps: newSteps.map((s, i) => ({ ...s, display_order: i + 1 })) };
    });
  };

  const handleSubmit = async () => {
    if (!formData.name) {
      alert('Please enter a flow name');
      return;
    }
    await onSave(formData);
  };

  const ModeIcon = MODE_CONFIG[formData.mode]?.icon || Settings;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-xl shadow-premium-modal w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-border">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${MODE_CONFIG[formData.mode]?.color || 'bg-gray-100'}`}>
              <ModeIcon className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{flow ? 'Edit Flow' : 'Create Flow'}</h3>
              <p className="text-sm text-gray-500">{MODE_CONFIG[formData.mode]?.label}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-secondary rounded">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Flow Name *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Standard Vinyl Builder"
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Mode</label>
              <select
                value={formData.mode}
                onChange={(e) => setFormData(prev => ({ ...prev, mode: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
                disabled={!!flow}
              >
                <option value="visual">Visual Builder</option>
                <option value="quick">Quick Order</option>
                <option value="expert">Expert Mode</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Product Family</label>
              <select
                value={formData.product_family}
                onChange={(e) => setFormData(prev => ({ ...prev, product_family: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
              >
                <option value="vinyl">Vinyl</option>
                <option value="cd">CD</option>
                <option value="dvd">DVD</option>
                <option value="bluray">Blu-ray</option>
              </select>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-gray-700">
                  Default Rate Card
                </label>
                <FamilyScopePicker
                  value={rateCardScope}
                  onChange={setRateCardScope}
                  activeFamily={formData.product_family}
                  label="Family"
                />
              </div>
              <select
                value={formData.default_rate_card_id}
                onChange={(e) => setFormData(prev => ({ ...prev, default_rate_card_id: e.target.value }))}
                className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
                data-testid="default-rate-card-select"
              >
                <option value="">None</option>
                {rateCards.map(card => {
                  const fam = (card.product_family || 'vinyl').toUpperCase();
                  const status = card.status ? ` · ${card.status}` : '';
                  return (
                    <option key={card.id} value={card.id}>
                      {card.name} [{fam}]{status}
                    </option>
                  );
                })}
              </select>
              {/* Cross-family warning chip — selection is allowed, just visible */}
              {isCrossFamily && (
                <div className="mt-1.5 flex items-center gap-1.5">
                  <CrossFamilyWarning
                    valueFamily={selectedRateCard.product_family || 'vinyl'}
                    activeFamily={formData.product_family}
                  />
                  <span className="text-[11px] text-amber-700">
                    Cross-family rate card — customers using this flow will be priced from the{' '}
                    {(selectedRateCard.product_family || 'vinyl').toUpperCase()} card.
                  </span>
                </div>
              )}
              {rateCards.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  No rate cards match the current scope. Widen Family above or create one in Sales Rate Cards.
                </p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
              placeholder="Internal description for this flow"
              rows={2}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-accent/20 focus:border-accent"
            />
          </div>

          {/* Steps */}
          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="text-sm font-medium text-gray-700">Configuration Steps ({formData.steps.length})</label>
              <button
                type="button"
                onClick={handleAddStep}
                disabled={!formData.default_rate_card_id}
                className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Plus className="w-4 h-4" />
                Add Step
              </button>
            </div>
            
            {!formData.default_rate_card_id && (
              <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-700">
                  <AlertCircle className="w-4 h-4 inline mr-1" />
                  Select a Default Rate Card above to enable pricing mapping for step options.
                </p>
              </div>
            )}
            
            {loadingItems && (
              <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">Loading rate card items...</p>
              </div>
            )}
            
            {formData.steps.length === 0 ? (
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Layers className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <p className="text-gray-500">No steps configured yet</p>
                <button
                  type="button"
                  onClick={handleAddStep}
                  disabled={!formData.default_rate_card_id}
                  className="mt-2 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
                >
                  Add your first step
                </button>
              </div>
            ) : (
              <div className="border border-border rounded-lg divide-y divide-border">
                {formData.steps.map((step, index) => (
                  <div key={step.step_id} className="flex items-center gap-3 p-3 hover:bg-secondary/30">
                    <div className="flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => moveStep(index, -1)}
                        disabled={index === 0}
                        className="p-0.5 hover:bg-secondary rounded disabled:opacity-30"
                      >
                        <ChevronDown className="w-4 h-4 rotate-180" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveStep(index, 1)}
                        disabled={index === formData.steps.length - 1}
                        className="p-0.5 hover:bg-secondary rounded disabled:opacity-30"
                      >
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </div>
                    <span className="w-6 h-6 flex items-center justify-center bg-indigo-100 text-indigo-700 text-xs font-medium rounded">
                      {index + 1}
                    </span>
                    <div className="flex-1">
                      <div className="font-medium">{step.label}</div>
                      <div className="text-xs text-gray-500">
                        {step.category_id || step.asset_category} • {step.ui_type} • {step.options?.length || 0} options
                        {!step.is_required && ' • Optional'}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => handleEditStep(step, index)}
                        className="p-1.5 hover:bg-secondary rounded text-gray-500 hover:text-gray-700"
                        data-testid={`edit-step-${index}`}
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteStep(index)}
                        className="p-1.5 hover:bg-red-50 rounded text-gray-500 hover:text-red-600"
                        data-testid={`delete-step-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status */}
          <div className="flex items-center gap-4 pt-4 border-t border-border">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData(prev => ({ ...prev, is_active: e.target.checked }))}
                className="rounded border-border"
              />
              <span className="text-sm text-gray-700">Active (visible to customers)</span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-4 border-t border-border bg-secondary/30">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 hover:bg-secondary rounded-lg"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
          >
            <Save className="w-4 h-4" />
            {flow ? 'Update Flow' : 'Create Flow'}
          </button>
        </div>
      </div>

      {/* Step Modal */}
      {showStepModal && (
        <StepEditorModal
          step={editingStep}
          assets={assets}
          rateCardItems={rateCardItems}
          attributeLists={attributeLists}
          otherSteps={formData.steps.filter((s) => s.step_id !== editingStep?.step_id)}
          productFamily={formData.product_family || 'vinyl'}
          onSave={handleSaveStep}
          onClose={() => { setShowStepModal(false); setEditingStep(null); }}
        />
      )}
    </div>
  );
}

// Main Page Component
export default function ProductConfigFlowsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [flows, setFlows] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [assets, setAssets] = useState([]);
  const [rateCards, setRateCards] = useState([]);
  const [attributeLists, setAttributeLists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'flows');
  const [editingFlow, setEditingFlow] = useState(null);
  const [showFlowEditor, setShowFlowEditor] = useState(false);

  // Phase 2: family scoping
  const [productFamily, setProductFamily] = useState('vinyl');
  const [flowFamilyCounts, setFlowFamilyCounts] = useState({ vinyl: 0, cd: 0, dvd: 0, bluray: 0 });
  const [bundleFamilyCounts, setBundleFamilyCounts] = useState({ vinyl: 0, cd: 0, dvd: 0, bluray: 0 });
  // Duplicate flow to family
  const [showDuplicateFlowModal, setShowDuplicateFlowModal] = useState(false);
  const [duplicateSourceFlow, setDuplicateSourceFlow] = useState(null);
  const [duplicateFlowBusy, setDuplicateFlowBusy] = useState(false);

  // Delete confirmation modal state
  const [deleteModal, setDeleteModal] = useState({
    open: false,
    flow: null,
    dependencies: null,
    loadingDeps: false,
    confirmText: '',
    isDeleting: false
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { 'Authorization': `Bearer ${getToken()}` };
      const fam = encodeURIComponent(productFamily);
      
      const [flowsRes, bundlesRes, assetsRes, cardsRes, listsRes] = await Promise.all([
        fetch(`${API_URL}/api/product-config/flows?active_only=false&product_family=${fam}`, { headers }),
        fetch(`${API_URL}/api/product-config/admin/bundles?product_family=${fam}`, { headers }),
        fetch(`${API_URL}/api/visual-builder/assets?active_only=false&product_family=${fam}`, { headers }),
        fetch(`${API_URL}/api/sales/rate-cards?product_family=${fam}`, { headers }),
        fetch(`${API_URL}/api/product-config/attribute-lists?product_family=${fam}`, { headers })
      ]);

      if (flowsRes.ok) setFlows(await flowsRes.json());
      if (bundlesRes.ok) setBundles(await bundlesRes.json());
      if (assetsRes.ok) setAssets(await assetsRes.json());
      if (cardsRes.ok) setRateCards(await cardsRes.json());
      if (listsRes.ok) setAttributeLists(await listsRes.json());
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  }, [productFamily]);

  const fetchCounts = useCallback(async () => {
    const families = ['vinyl', 'cd', 'dvd', 'bluray'];
    const headers = { 'Authorization': `Bearer ${getToken()}` };
    try {
      const flowResults = await Promise.all(
        families.map(fam =>
          fetch(`${API_URL}/api/product-config/flows?active_only=false&product_family=${fam}`, { headers })
            .then(r => r.ok ? r.json() : [])
            .then(d => [fam, (d || []).length])
            .catch(() => [fam, 0])
        )
      );
      setFlowFamilyCounts(Object.fromEntries(flowResults));
      const bundleResults = await Promise.all(
        families.map(fam =>
          fetch(`${API_URL}/api/product-config/admin/bundles?product_family=${fam}`, { headers })
            .then(r => r.ok ? r.json() : [])
            .then(d => [fam, (d || []).length])
            .catch(() => [fam, 0])
        )
      );
      setBundleFamilyCounts(Object.fromEntries(bundleResults));
    } catch (err) {
      console.error('Failed to fetch family counts:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchCounts();
  }, [fetchData, fetchCounts]);

  const handleCreateFlow = () => {
    setEditingFlow(null);
    setShowFlowEditor(true);
  };

  const handleEditFlow = (flow) => {
    setEditingFlow(flow);
    setShowFlowEditor(true);
  };

  const handleSaveFlow = async (flowData) => {
    try {
      const url = editingFlow
        ? `${API_URL}/api/product-config/admin/flows/${editingFlow.id}`
        : `${API_URL}/api/product-config/admin/flows`;
      // Phase 2: ensure new flows pick up the currently-active product_family
      const payload = editingFlow ? flowData : { product_family: productFamily, ...flowData };
      
      const res = await fetch(url, {
        method: editingFlow ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        let msg = 'Failed to save flow';
        if (Array.isArray(err?.detail)) {
          // FastAPI 422 — each entry is {loc, msg, type}. Render a
          // short, readable list instead of "[object Object]".
          msg = err.detail
            .map((e) => {
              const loc = Array.isArray(e?.loc)
                ? e.loc.filter((p) => p !== 'body').join('.')
                : '';
              const text = e?.msg || e?.type || 'Invalid value';
              return loc ? `${loc}: ${text}` : text;
            })
            .join('\n');
        } else if (typeof err?.detail === 'string') {
          msg = err.detail;
        } else if (err?.detail && typeof err.detail === 'object') {
          msg = err.detail.msg || JSON.stringify(err.detail);
        }
        throw new Error(msg);
      }

      setShowFlowEditor(false);
      setEditingFlow(null);
      fetchData();
      fetchCounts();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDuplicateFlowToFamily = async (payload) => {
    if (!duplicateSourceFlow) return;
    setDuplicateFlowBusy(true);
    try {
      const r = await fetch(
        `${API_URL}/api/product-config/admin/flows/${duplicateSourceFlow.id}/duplicate-to-family`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${getToken()}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(payload),
        }
      );
      if (!r.ok) {
        const err = await r.json();
        throw new Error(err.detail || 'Duplicate failed');
      }
      const result = await r.json();
      setShowDuplicateFlowModal(false);
      setDuplicateSourceFlow(null);
      setProductFamily(payload.product_family);
      fetchCounts();
      alert(`New ${payload.product_family.toUpperCase()} flow: "${result.new_flow_name}" (${result.steps_copied} steps copied).`);
    } catch (err) {
      alert(`Duplicate failed: ${err.message}`);
    } finally {
      setDuplicateFlowBusy(false);
    }
  };

  const handleDuplicateFlow = async (flow) => {
    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/flows/${flow.id}/duplicate`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      if (!res.ok) throw new Error('Failed to duplicate flow');
      fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  // Open delete modal and fetch dependencies
  const handleDeleteFlow = async (flow) => {
    setDeleteModal({
      open: true,
      flow: flow,
      dependencies: null,
      loadingDeps: true,
      confirmText: '',
      isDeleting: false
    });
    
    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/flows/${flow.id}/dependencies`, {
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });
      
      if (res.ok) {
        const deps = await res.json();
        setDeleteModal(prev => ({ ...prev, dependencies: deps, loadingDeps: false }));
      } else {
        setDeleteModal(prev => ({ ...prev, loadingDeps: false }));
      }
    } catch (err) {
      console.error('Failed to fetch dependencies:', err);
      setDeleteModal(prev => ({ ...prev, loadingDeps: false }));
    }
  };
  
  // Confirm and execute deletion
  const confirmDeleteFlow = async () => {
    if (deleteModal.confirmText !== 'DELETE') {
      return;
    }
    
    setDeleteModal(prev => ({ ...prev, isDeleting: true }));
    
    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/flows/${deleteModal.flow.id}/delete-confirmed`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirm_text: 'DELETE' })
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Failed to delete flow');
      }
      
      setDeleteModal({ open: false, flow: null, dependencies: null, loadingDeps: false, confirmText: '', isDeleting: false });
      fetchData();
    } catch (err) {
      alert(err.message);
      setDeleteModal(prev => ({ ...prev, isDeleting: false }));
    }
  };
  
  // Close delete modal
  const closeDeleteModal = () => {
    if (deleteModal.isDeleting) return; // Prevent closing while deleting
    setDeleteModal({ open: false, flow: null, dependencies: null, loadingDeps: false, confirmText: '', isDeleting: false });
  };

  const handleToggleFlowActive = async (flow) => {
    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/flows/${flow.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${getToken()}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ is_active: !flow.is_active })
      });

      if (!res.ok) throw new Error('Failed to update flow');
      fetchData();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleSeedDefaultFlow = async () => {
    try {
      const res = await fetch(`${API_URL}/api/product-config/admin/seed-default-flow`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${getToken()}` }
      });

      const data = await res.json();
      if (data.created) {
        alert('Default Visual Builder flow created successfully!');
        fetchData();
      } else {
        alert(data.message);
      }
    } catch (err) {
      alert('Failed to seed default flow');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="product-config-flows-page">
      <Header />
      {/* Page title + Seed Defaults — old back button lived here; global
          Admin Header now handles cross-section navigation. */}
      <div className="bg-card border-b border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900">Product Config Flows</h1>
              <p className="text-sm text-gray-500">Configure ordering modes, steps, and options</p>
            </div>
            <button
              onClick={handleSeedDefaultFlow}
              className="flex items-center gap-2 px-3 py-2 text-gray-600 hover:bg-secondary rounded-lg text-sm"
            >
              <Package className="w-4 h-4" />
              Seed Defaults
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center justify-between mb-4">
          <ProductFamilyTabs
            value={productFamily}
            onChange={(fam) => setProductFamily(fam)}
            counts={activeTab === 'flows' ? flowFamilyCounts : bundleFamilyCounts}
          />
          <span className="text-xs text-slate-500">
            Showing <span className="font-medium text-slate-700">{getProductFamilyMeta(productFamily).label}</span>{' '}
            {activeTab === 'flows' ? 'flows' : 'quick bundles'}
          </span>
        </div>
        <div className="flex gap-4 mb-6">
          <button
            onClick={() => setActiveTab('flows')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'flows'
                ? 'bg-indigo-100 text-indigo-700'
                : 'text-gray-600 hover:bg-secondary'
            }`}
          >
            <Layers className="w-4 h-4" />
            Flows ({flows.length})
          </button>
          <button
            onClick={() => setActiveTab('bundles')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors ${
              activeTab === 'bundles'
                ? 'bg-amber-100 text-amber-700'
                : 'text-gray-600 hover:bg-secondary'
            }`}
          >
            <ShoppingBag className="w-4 h-4" />
            Quick Bundles ({bundles.length})
          </button>
        </div>

        {/* Flows Grid */}
        {activeTab === 'flows' && (
          <div className="space-y-4">
            {/* Header with New Flow button */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-slate-500">
                Configure ordering modes, steps, and options for Visual Builder and Expert modes
              </p>
              <button
                onClick={handleCreateFlow}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                data-testid="create-flow-btn"
              >
                <Plus className="w-4 h-4" />
                New Flow
              </button>
            </div>
            
            {flows.length === 0 ? (
              <div className="bg-card rounded-xl border border-border p-12 text-center">
                <Layers className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">No flows configured</h3>
                <p className="text-gray-500 mb-4">Create your first product configuration flow to get started.</p>
                <div className="flex gap-3 justify-center">
                  <button
                    onClick={handleSeedDefaultFlow}
                    className="flex items-center gap-2 px-4 py-2 border border-border rounded-lg hover:bg-secondary"
                  >
                    <Package className="w-4 h-4" />
                    Seed Default Flow
                  </button>
                  <button
                    onClick={handleCreateFlow}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                  >
                    <Plus className="w-4 h-4" />
                    Create Custom Flow
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid gap-4">
                {flows.map(flow => {
                  const ModeIcon = MODE_CONFIG[flow.mode]?.icon || Settings;
                  const modeColor = MODE_CONFIG[flow.mode]?.color || 'bg-gray-100';
                  const configIssue = flow._config_issue;

                  return (
                    <div
                      key={flow.id}
                      className={`bg-card rounded-xl border shadow-premium-card hover:shadow-premium-lg transition-shadow ${
                        configIssue
                          ? 'border-amber-400 ring-1 ring-amber-200'
                          : flow.is_active ? 'border-border' : 'border-border opacity-60'
                      }`}
                      data-testid={`flow-card-${flow.id}`}
                    >
                      {configIssue && (
                        <div
                          className="rounded-t-xl bg-amber-50 border-b border-amber-200 px-4 py-2 flex items-start gap-2"
                          data-testid={`flow-config-issue-${flow.id}`}
                        >
                          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                          <div className="text-xs text-amber-800 leading-snug">
                            <div className="font-semibold">
                              Configuration issue — flow needs manual repair
                            </div>
                            <div className="mt-0.5">
                              Missing or invalid fields:{' '}
                              <span className="font-mono">
                                {(configIssue.missing_or_invalid_fields || []).join(', ') || 'unknown'}
                              </span>
                            </div>
                            {configIssue.errors && configIssue.errors.length > 0 && (
                              <ul className="mt-1 list-disc pl-4 space-y-0.5">
                                {configIssue.errors.slice(0, 4).map((err, idx) => (
                                  <li key={idx}>
                                    <span className="font-mono">{err.field}</span>: {err.message}
                                  </li>
                                ))}
                                {configIssue.errors.length > 4 && (
                                  <li className="italic">
                                    +{configIssue.errors.length - 4} more…
                                  </li>
                                )}
                              </ul>
                            )}
                          </div>
                        </div>
                      )}
                      <div className="p-5">
                        <div className="flex items-start gap-4">
                          <div className={`p-3 rounded-lg ${modeColor}`}>
                            <ModeIcon className="w-6 h-6" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-semibold text-gray-900">{flow.name}</h3>
                              {flow.is_active ? (
                                <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">Active</span>
                              ) : (
                                <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs font-medium rounded-full">Inactive</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 mt-1">
                              {MODE_CONFIG[flow.mode]?.label} • {flow.product_family || <span className="text-amber-700 font-semibold">missing product family</span>} • {flow.steps?.length || 0} steps
                            </p>
                            {flow.description && (
                              <p className="text-sm text-gray-600 mt-2">{flow.description}</p>
                            )}
                          </div>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleToggleFlowActive(flow)}
                              className={`p-2 rounded-lg ${flow.is_active ? 'hover:bg-gray-100 text-green-600' : 'hover:bg-gray-100 text-gray-400'}`}
                              title={flow.is_active ? 'Deactivate' : 'Activate'}
                            >
                              {flow.is_active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                            </button>
                            <button
                              onClick={() => handleDuplicateFlow(flow)}
                              className="p-2 hover:bg-secondary rounded-lg text-gray-500"
                              title="Duplicate (same family)"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => { setDuplicateSourceFlow(flow); setShowDuplicateFlowModal(true); }}
                              className="p-2 hover:bg-indigo-50 rounded-lg text-indigo-600"
                              title="Duplicate to product family"
                              data-testid={`duplicate-flow-to-family-${flow.id}`}
                            >
                              <Layers className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleEditFlow(flow)}
                              className="p-2 hover:bg-secondary rounded-lg text-gray-500"
                              title="Edit"
                              data-testid={`edit-flow-${flow.id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteFlow(flow)}
                              className="p-2 hover:bg-red-50 rounded-lg text-gray-500 hover:text-red-600"
                              title="Delete"
                              data-testid={`delete-flow-${flow.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>

                        {/* Steps Preview */}
                        {flow.steps?.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-border">
                            <div className="flex flex-wrap gap-2">
                              {flow.steps.map((step, idx) => (
                                <div
                                  key={step.step_id}
                                  className="flex items-center gap-1 px-2 py-1 bg-secondary rounded text-xs"
                                >
                                  <span className="w-4 h-4 flex items-center justify-center bg-indigo-100 text-indigo-700 rounded text-[10px] font-medium">
                                    {idx + 1}
                                  </span>
                                  <span className="text-gray-700">{step.label}</span>
                                  <span className="text-gray-400">({step.options?.length || 0})</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Bundles Tab - Using shared QuickBundlesManager component */}
        {activeTab === 'bundles' && (
          <QuickBundlesManager bundles={bundles} rateCards={rateCards} productFamily={productFamily} />
        )}
      </div>

      {/* Flow Editor Modal */}
      {showFlowEditor && (
        <FlowEditor
          flow={editingFlow}
          assets={assets}
          rateCards={rateCards}
          attributeLists={attributeLists}
          activeFamily={productFamily}
          onSave={handleSaveFlow}
          onClose={() => { setShowFlowEditor(false); setEditingFlow(null); }}
        />
      )}

      {/* Duplicate Flow to Family Dialog */}
      <DuplicateToFamilyDialog
        open={showDuplicateFlowModal}
        onClose={() => { setShowDuplicateFlowModal(false); setDuplicateSourceFlow(null); }}
        sourceLabel={duplicateSourceFlow?.name || ''}
        sourceFamily={duplicateSourceFlow?.product_family || 'vinyl'}
        entityType="flow"
        busy={duplicateFlowBusy}
        onConfirm={handleDuplicateFlowToFamily}
      />
      
      {/* Delete Confirmation Modal */}
      <AlertDialog open={deleteModal.open} onOpenChange={(open) => !open && closeDeleteModal()}>
        <AlertDialogContent className="max-w-lg" data-testid="delete-flow-modal">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="h-5 w-5" />
              Delete Product Config Flow
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4">
                <p className="text-slate-600">
                  You are about to delete the flow: <strong className="text-slate-900">{deleteModal.flow?.name}</strong>
                </p>
                
                {/* Dependencies Loading */}
                {deleteModal.loadingDeps && (
                  <div className="flex items-center gap-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                    <Loader2 className="h-4 w-4 animate-spin text-slate-500" />
                    <span className="text-sm text-slate-600">Checking for dependent orders...</span>
                  </div>
                )}
                
                {/* Dependencies Warning */}
                {deleteModal.dependencies && deleteModal.dependencies.has_dependencies && (
                  <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-medium text-amber-800">
                          This flow is used by {deleteModal.dependencies.dependent_orders_count} order(s)
                        </p>
                        <p className="text-amber-700 mt-1">
                          Deleting this flow will not affect existing orders, but it will no longer be available for new orders.
                        </p>
                        {deleteModal.dependencies.dependent_orders?.length > 0 && (
                          <div className="mt-2 max-h-32 overflow-y-auto">
                            <p className="text-xs text-amber-600 mb-1">Recent orders using this flow:</p>
                            <ul className="text-xs space-y-0.5">
                              {deleteModal.dependencies.dependent_orders.slice(0, 5).map(order => (
                                <li key={order.id} className="text-amber-700">
                                  #{order.order_number?.toString().padStart(6, '0')} - {order.title || 'Untitled'} ({order.status})
                                </li>
                              ))}
                              {deleteModal.dependencies.dependent_orders_count > 5 && (
                                <li className="text-amber-500 italic">
                                  ...and {deleteModal.dependencies.dependent_orders_count - 5} more
                                </li>
                              )}
                            </ul>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {/* No Dependencies */}
                {deleteModal.dependencies && !deleteModal.dependencies.has_dependencies && (
                  <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                    <div className="flex items-center gap-2">
                      <Check className="h-4 w-4 text-emerald-600" />
                      <span className="text-sm text-emerald-700">No orders are using this flow.</span>
                    </div>
                  </div>
                )}
                
                {/* Confirmation Input */}
                <div className="pt-2 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-2">
                    To confirm deletion, type <strong className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-red-600">DELETE</strong> below:
                  </p>
                  <Input
                    type="text"
                    value={deleteModal.confirmText}
                    onChange={(e) => setDeleteModal(prev => ({ ...prev, confirmText: e.target.value }))}
                    placeholder="Type DELETE to confirm"
                    className="font-mono"
                    data-testid="delete-confirm-input"
                    disabled={deleteModal.isDeleting}
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="mt-4">
            <Button
              variant="outline"
              onClick={closeDeleteModal}
              disabled={deleteModal.isDeleting}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmDeleteFlow}
              disabled={deleteModal.confirmText !== 'DELETE' || deleteModal.isDeleting || deleteModal.loadingDeps}
              data-testid="confirm-delete-btn"
            >
              {deleteModal.isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Flow
                </>
              )}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
